import { safeZodPath } from "@impero-com/common/utils/form/safeZodPath";
import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { getTimezoneNames } from "@impero-com/common/utils/timezone";
import z from "zod";
import { ParsePayload } from "zod/v4/core";

import {
    getAllocatedTimeSchema,
    getDaysValueSchema,
} from "components/form/rhfFields/rhfAllocatedTimeInput";

import { TestingControlPageType } from "./types";

const MAX_TITLE = 255;
const MAX_DESCRIPTION = 100000;

export const getTitleSchema = (t: TFunction) =>
    z
        .string({
            error: (issue) =>
                issue.input === undefined
                    ? t("Validation_Required")
                    : t("Validation_Invalid"),
        })
        .trim()
        .min(1, { error: t("Validation_Required") })
        .max(MAX_TITLE);
export const descriptionSchema = z.string().trim().max(MAX_DESCRIPTION);
export const getDueDateSchema = (t: TFunction) =>
    z.date({
        error: (issue) =>
            issue.input === undefined
                ? t("Validation_Required")
                : t("Validation_Invalid"),
    });

const getPeriodDateSchema = (t: TFunction) =>
    z.date({
        error: () => t("Validation_Required"),
    });

const getUserIdSchema = (t: TFunction) =>
    z.number({
        error: (issue) =>
            issue.input === undefined
                ? t("Validation_Required")
                : t("Validation_Invalid"),
    });

export const randomSampleCountSchema = z.number().int().min(0); //eslint-disable-line zod/no-number-schema-with-int

interface AllocatedTime {
    unit: "Days" | "Hours";
    value: number;
}
interface Assignee {
    userId: number;
    allocatedTime: AllocatedTime;
}

type OptionalAssigneeSchema = z.infer<
    ReturnType<typeof getUnrefinedOptionalAssigneeSchema>
>;

function getUnrefinedOptionalAssigneeSchema(t: TFunction) {
    return z.optional(
        z
            .object({
                userId: z.nullable(getUserIdSchema(t)).optional(),
                allocatedTime: getAllocatedTimeSchema(t),
            })
            .transform((data) => (data?.userId ? data : undefined))
    );
}

export const notificationTimeSchema = z.date();

// Common schemas
const getCommonSchemas = (t: TFunction) => {
    const allocatedTimeSchema = getAllocatedTimeSchema(t);
    const unrefinedResponsibleSchema = z.object({
        userId: getUserIdSchema(t),
        allocatedTime: z.object({
            unit: z.literal("Days"),
            value: getDaysValueSchema(),
        }),
    });
    const responsibleSchema = unrefinedResponsibleSchema.check((ctx) =>
        refineAssignee(ctx.value, ctx, t)
    );

    const unrefinedOptionalAssigneeSchema =
        getUnrefinedOptionalAssigneeSchema(t);
    const optionalAssigneeSchema = unrefinedOptionalAssigneeSchema.check(
        (ctx) => refineAssignee(ctx.value, ctx, t)
    );

    const unrefinedPeriodObjSchema = z.object({
        start: getPeriodDateSchema(t),
        end: getPeriodDateSchema(t),
    });
    const periodObjSchema = unrefinedPeriodObjSchema.check((ctx) => {
        const { start, end } = ctx.value;
        if (start > end) {
            ctx.issues.push({
                message: t("TestingControlView_Period_EndBeforeStartError"),
                code: "custom",
                input: ctx.value,
            });
        }
    });

    const unrefinedReviewersSchema = z.object({
        reviewer: optionalAssigneeSchema,
        secondReviewer: optionalAssigneeSchema,
    });
    type ReviewersSchema = z.infer<typeof unrefinedReviewersSchema>;
    const reviewersSchema = unrefinedReviewersSchema.check((ctx) => {
        if (ctx.value.secondReviewer && !ctx.value.reviewer) {
            ctx.issues.push({
                message: t("Validation_Required"),
                code: "custom",
                input: ctx.value,
                path: safeZodPath<ReviewersSchema>(`reviewer.userId`),
            });
        }
    });

    const timezones = Object.values(getTimezoneNames());
    const timezoneNameSchema = z.enum(
        timezones as [(typeof timezones)[number], ...typeof timezones]
    );
    const remindersSchema = z.optional(z.array(z.number().min(1).max(365)));

    return {
        allocatedTimeSchema,
        responsibleSchema,
        periodObjSchema,
        reviewersSchema,
        timezoneNameSchema,
        remindersSchema,
    };
};

// Helper function for assignee refinement
function refineAssignee(
    data: OptionalAssigneeSchema,
    ctx: ParsePayload,
    t: TFunction
) {
    if (data && data.userId) {
        if (!data.allocatedTime) {
            ctx.issues.push({
                path: safeZodPath<Assignee>(`allocatedTime`),
                message: t("Validation_Required"),
                code: "custom",
                input: data,
            });
        }
    }
}

// Page type specific schemas
const getEditSchema = (t: TFunction) => {
    const common = getCommonSchemas(t);
    return z.object({
        controlId: z
            .guid({
                error: (issue) =>
                    issue.input === undefined
                        ? t("Validation_Required")
                        : t("Validation_Invalid"),
            })
            .optional(),
        tagIds: z.array(z.guid()),
        title: getTitleSchema(t),
        description: descriptionSchema,
        samplingTagIds: z
            .array(z.guid())
            .min(1, t("TestingControlView_EntityTags_Empty")),
        period: common.periodObjSchema,
        responsible: common.responsibleSchema,
        notificationTime: notificationTimeSchema,
        reviewers: common.reviewersSchema,
        dueDate: getDueDateSchema(t),
        timezoneName: common.timezoneNameSchema,
        remindersBefore: common.remindersSchema,
        remindersAfter: common.remindersSchema,
        randomSampleCount: randomSampleCountSchema,
    });
};

const getCreateSchema = (t: TFunction) => {
    const common = getCommonSchemas(t);
    return z.object({
        controlId: z.guid({
            error: (issue) =>
                issue.input === undefined
                    ? t("Validation_Required")
                    : t("Validation_Invalid"),
        }),
        tagIds: z.array(z.guid()),
        title: getTitleSchema(t),
        description: descriptionSchema,
        samplingTagIds: z
            .array(z.guid())
            .min(1, t("TestingControlView_EntityTags_Empty")),
        period: common.periodObjSchema,
        responsible: common.responsibleSchema,
        notificationTime: notificationTimeSchema,
        reviewers: common.reviewersSchema,
        dueDate: getDueDateSchema(t),
        timezoneName: common.timezoneNameSchema,
        remindersBefore: common.remindersSchema,
        remindersAfter: common.remindersSchema,
        randomSampleCount: randomSampleCountSchema,
    });
};

const getBulkCreateSchema = (t: TFunction) => {
    const common = getCommonSchemas(t);
    return z.object({
        selectedControlIds: z.array(z.guid()),
        samplingTagIds: z
            .array(z.guid())
            .min(1, t("TestingControlView_EntityTags_Empty")),
        period: common.periodObjSchema,
        responsible: common.responsibleSchema,
        notificationTime: notificationTimeSchema,
        reviewers: common.reviewersSchema,
        dueDate: getDueDateSchema(t),
        timezoneName: common.timezoneNameSchema,
        remindersBefore: common.remindersSchema,
        remindersAfter: common.remindersSchema,
        randomSampleCount: randomSampleCountSchema,
    });
};

export function getTestingControlSchema(
    pageType: TestingControlPageType,
    t: TFunction
) {
    switch (pageType) {
        case "EDIT":
            return getEditSchema(t);
        case "CREATE":
            return getCreateSchema(t);
        case "BULK_CREATE":
            return getBulkCreateSchema(t);
        default:
            return assertNever(pageType);
    }
}

export type TestingControlFormSchema = z.infer<
    ReturnType<typeof getTestingControlSchema>
>;
export type TestingControlEditSchema = z.infer<
    ReturnType<typeof getEditSchema>
>;
export type TestingControlCreateSchema = z.infer<
    ReturnType<typeof getCreateSchema>
>;
export type TestingControlBulkCreateSchema = z.infer<
    ReturnType<typeof getBulkCreateSchema>
>;

export type TestingControlTimezone = TestingControlFormSchema["timezoneName"];
